import Decimal from 'decimal.js';

/**
 * Stock lots: pure decision logic.
 *
 * A LOT is one receipt of a product at a branch, with its own cost and
 * (optionally) expiry date. Lots are additive: the branch's stock counter stays
 * the source of truth for what can be sold, and a lot only says what that
 * quantity is made of. Stock held before lots were turned on has no lot; it is
 * the UNLOTTED pool, `on hand − Σ lot remaining`.
 *
 * Three rules live here so the API, its tests and the admin screens agree:
 *
 *   1. WHICH lots a stock-out takes from (`planLotPick`).
 *   2. WHAT a sold line cost, given what it took (`costFromChunks`).
 *   3. HOW close to expiry a lot is (`expiryBucket`).
 *
 * This module is pure (no DB). The DB-aware half is
 * `apps/api/src/services/stock-lots.ts`.
 */

export const LOT_PICK_ORDERS = ['fifo', 'fefo'] as const;
export type LotPickOrder = (typeof LOT_PICK_ORDERS)[number];

export type PickableLot = {
  id: string;
  /** ISO timestamp or Date; only its order matters. */
  receivedAt: Date | string;
  /** `YYYY-MM-DD`, or null when the lot does not expire. */
  expiresOn: string | null;
  qtyRemaining: number;
};

export type LotPick = {
  /** NULL = taken from the unlotted pool. */
  lotId: string | null;
  qty: number;
};

function receivedMs(lot: PickableLot): number {
  return lot.receivedAt instanceof Date ? lot.receivedAt.getTime() : Date.parse(lot.receivedAt);
}

function byReceived(a: PickableLot, b: PickableLot): number {
  return receivedMs(a) - receivedMs(b) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

/**
 * Decide which lots a stock-out of `need` units takes from.
 *
 * FIFO: the unlotted pool goes first. It is stock that was on the shelf before
 * the first lot was ever recorded, so it is by definition the oldest, and
 * draining it first is also what moves a tenant onto fully lotted stock. Then
 * the lots, oldest received first.
 *
 * FEFO: lots that carry an expiry date go first, soonest first (ties broken by
 * received order). Then exactly the FIFO order for everything that does not
 * expire: the unlotted pool, then the undated lots.
 *
 * The plan never fails. The stock counter has already said the units exist, so
 * if the lots and the pool together come up short (lots drifted while the
 * switch was off), the remainder is booked to the unlotted pool rather than
 * blocking a sale over bookkeeping.
 */
export function planLotPick(args: {
  lots: PickableLot[];
  order: LotPickOrder;
  need: number;
  /** Units on hand that belong to no lot. Negative is treated as zero. */
  unlotted: number;
}): LotPick[] {
  if (!Number.isInteger(args.need) || args.need <= 0) return [];
  const open = args.lots.filter((l) => l.qtyRemaining > 0);
  const dated =
    args.order === 'fefo'
      ? open
          .filter((l) => l.expiresOn != null)
          .sort((a, b) =>
            a.expiresOn! < b.expiresOn! ? -1 : a.expiresOn! > b.expiresOn! ? 1 : byReceived(a, b),
          )
      : [];
  const datedIds = new Set(dated.map((l) => l.id));
  const rest = open.filter((l) => !datedIds.has(l.id)).sort(byReceived);

  const picks: LotPick[] = [];
  let left = args.need;
  const takeLot = (lot: PickableLot) => {
    if (left <= 0) return;
    const qty = Math.min(lot.qtyRemaining, left);
    if (qty <= 0) return;
    picks.push({ lotId: lot.id, qty });
    left -= qty;
  };

  for (const lot of dated) takeLot(lot);
  let fromPool = Math.min(Math.max(args.unlotted, 0), left);
  left -= fromPool;
  const poolAt = picks.length;
  for (const lot of rest) takeLot(lot);
  // Whatever nothing could cover is booked to the pool too (see above).
  fromPool += left;
  if (fromPool > 0) picks.splice(poolAt, 0, { lotId: null, qty: fromPool });
  return picks;
}

export type CostChunk = {
  lotId: string | null;
  qty: number;
  /** Per-unit cost of this chunk; null = uncosted. */
  unitCost: Decimal.Value | null;
};

/**
 * What a set of consumed chunks cost in total, and how many of the units had
 * no cost at all.
 */
export function chunksCost(chunks: CostChunk[]): { total: Decimal; uncostedQty: number } {
  let total = new Decimal(0);
  let uncostedQty = 0;
  for (const c of chunks) {
    if (c.unitCost == null) uncostedQty += c.qty;
    else total = total.plus(new Decimal(c.unitCost).mul(c.qty));
  }
  return { total, uncostedQty };
}

/**
 * The actual per-unit cost of a sold line, from the chunks its stock deduction
 * consumed, or null when the static snapshot should stand.
 *
 * It stands when no real lot was involved: units taken only from the unlotted
 * pool are costed at the static cost, which is what the checkout snapshot
 * already holds (and for a kit assembled from parts, the kit's own cost is the
 * figure the tenant chose, not the sum of its parts). Once a real lot is in the
 * mix the line is costed at the weighted total of everything it took, divided
 * by the units SOLD (a kit sold from 3 parts is still 1 unit).
 *
 * It also stands when ANY piece had no cost. Uncosted is recorded as NULL,
 * never as 0: summing the costed pieces and calling that the line's cost would
 * understate it (or stamp a flat 0 on a line nobody ever costed), and the
 * books would stop flagging the line as uncosted.
 */
export function lineCostFromChunks(chunks: CostChunk[], lineQty: number): Decimal | null {
  if (lineQty <= 0) return null;
  if (!chunks.some((c) => c.lotId != null)) return null;
  const { total, uncostedQty } = chunksCost(chunks);
  if (uncostedQty > 0) return null;
  return total.div(lineQty).toDecimalPlaces(4);
}

/** The soonest of several `YYYY-MM-DD` expiry dates; null when none is dated. */
export function earliestExpiry(dates: Array<string | null | undefined>): string | null {
  let min: string | null = null;
  for (const d of dates) if (d && (min === null || d < min)) min = d;
  return min;
}

/**
 * Per-unit cost of the pieces that come out of an opened bundle: the bundle's
 * cost spread evenly over every piece inside it. Null stays null.
 */
export function openedPieceCost(
  bundleUnitCost: Decimal.Value | null,
  piecesPerBundle: number,
): Decimal | null {
  if (bundleUnitCost == null || piecesPerBundle <= 0) return null;
  return new Decimal(bundleUnitCost).div(piecesPerBundle).toDecimalPlaces(4);
}

const DAY_MS = 86_400_000;

function dayNumber(isoDate: string): number {
  const [y, m, d] = isoDate.slice(0, 10).split('-').map(Number);
  return Math.floor(Date.UTC(y!, (m ?? 1) - 1, d ?? 1) / DAY_MS);
}

/** Whole days from `today` to `expiresOn` (both `YYYY-MM-DD`). Negative = past. */
export function daysUntilExpiry(expiresOn: string, today: string): number {
  return dayNumber(expiresOn) - dayNumber(today);
}

/** `received + shelfLifeDays`, as `YYYY-MM-DD`. */
export function shelfLifeExpiry(receivedOn: string, shelfLifeDays: number): string {
  return new Date((dayNumber(receivedOn) + shelfLifeDays) * DAY_MS).toISOString().slice(0, 10);
}

export const EXPIRY_BUCKETS = ['expired', 'within_7', 'within_30', 'later', 'none'] as const;
export type ExpiryBucket = (typeof EXPIRY_BUCKETS)[number];

/**
 * How urgent a lot's expiry is. A lot that expires TODAY is still good today,
 * so it is `within_7`, not `expired`.
 */
export function expiryBucket(expiresOn: string | null, today: string): ExpiryBucket {
  if (!expiresOn) return 'none';
  const days = daysUntilExpiry(expiresOn, today);
  if (days < 0) return 'expired';
  if (days <= 7) return 'within_7';
  if (days <= 30) return 'within_30';
  return 'later';
}

/**
 * Receiving by the pack: a material is stocked in its smallest unit but bought
 * by the bag or box (`pack_size` units each). Turns "3 bags at 450.00 a bag"
 * into the units and per-unit cost the stock card holds. A product with no pack
 * size is passed through unchanged.
 */
export function receiveByPack(args: {
  packs: number;
  packSize: number | null | undefined;
  packCost?: Decimal.Value | null;
}): { qty: number; unitCost: Decimal | null } {
  const size = args.packSize && args.packSize > 0 ? args.packSize : 1;
  const packCost = args.packCost;
  return {
    qty: args.packs * size,
    unitCost:
      packCost == null || packCost === ''
        ? null
        : new Decimal(packCost).div(size).toDecimalPlaces(4),
  };
}
